// Cave84 — catalog name keys (pure; shared by the MWC import page and scripts).
//
// One definition of "is this the same producer / the same wine", so the in-app
// importer, the reconciliation scripts and merge:producers can never disagree.
//
//   producerKey("Domaine Armand Rousseau Père et Fils") === producerKey("Armand Rousseau")
//   producerKey("Ch. Pavie Decesse")                    === producerKey("Château Pavie Decesse")
//   producerKey("Egon Müller, Le Gallais")              !== producerKey("Egon Müller")
//
// Cuvée identity compares only DISTINCTIVE words: classification words and the
// producer's own name are removed, and two cuvées match only on Jaccard > 0.5 —
// a shared commune name ("Chambertin") is never enough.

const STOP = new Set([
  'de', 'du', 'des', 'la', 'le', 'les', 'di', 'del', 'della', 'von', 'van', 'the', 'et', 'and',
  'domaine', 'domain', 'dom', 'chateau', 'ch', 'weingut', 'maison', 'tenuta', 'bodegas', 'cru', 'aoc',
]);

/** Estate prefixes stripped from a producer name typed into free text. */
const ESTATE_WORDS = new Set(['domaine', 'domain', 'dom', 'chateau', 'ch', 'weingut', 'maison', 'tenuta', 'bodegas', 'et', 'and']);

/** Words that don't distinguish one producer from another. */
const PRODUCER_NOISE = new Set(['fils', 'pere', 'peres', 'freres', 'frere', 'sons', 'figli', 'hijos', 'erben']);

// Classification / boilerplate words that say nothing about WHICH wine it is.
const GENERIC = new Set([
  'grand', 'grande', 'cru', 'crus', '1er', 'premier', 'classe', 'ac', 'aoc', 'doc', 'docg',
  'reserve', 'reserva', 'gran', 'vv', 'vieilles', 'vignes', 'les', 'le', 'la', 'aux', 'en',
  'ribera', 'duero', 'rioja', 'eme', '2eme', '3eme', '4eme', '5eme', 'zweitwein', 'von',
]);

export function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/ß/g, 'ss').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function tokens(s: string): Set<string> {
  return new Set(fold(s).split(' ').filter(t => t.length > 1 && !STOP.has(t)));
}

/** Canonical identity of a producer name. Equal keys = same producer. */
export function producerKey(name: string): string {
  return [...tokens(name)].filter(t => !PRODUCER_NOISE.has(t)).sort().join(' ');
}

/** The words of a cuvée that identify it, minus classification words and `exclude` (producer tokens). */
export function distinct(s: string, exclude: Set<string>): string {
  return [...tokens(s)].filter(t => !GENERIC.has(t) && !exclude.has(t)).join(' ');
}

/**
 * Match score for two DISTINCTIVE cuvée strings: Jaccard similarity of their
 * token sets. Both empty (e.g. "Pauillac 1er Cru Classe" vs a Château with no
 * cuvée) → 1; exactly one empty → 0.6 (weak, needs a unique candidate).
 */
export function cuveeScore(a: string, c: string): number {
  if (!a && !c) return 1;
  if (!a || !c) return 0.6;
  const A = new Set(a.split(' ').filter(Boolean)), C = new Set(c.split(' ').filter(Boolean));
  const inter = [...A].filter(t => C.has(t)).length;
  return inter / new Set([...A, ...C]).size;
}

/** Cuvée identity under a producer: two wines are the same wine when these are equal. */
export function cuveeKey(cuvee: string, producerNames: string[]): string {
  const exclude = new Set(producerNames.flatMap(n => [...tokens(n)]));
  return distinct(cuvee, exclude).split(' ').filter(Boolean).sort().join(' ');
}

/** Share of the smaller token set found in the larger (0..1). */
export function overlap(a: string, b: string): number {
  const A = tokens(a), B = tokens(b);
  if (A.size === 0 || B.size === 0) return 0;
  const [small, big] = A.size <= B.size ? [A, B] : [B, A];
  let hit = 0;
  for (const t of small) if (big.has(t)) hit++;
  return hit / small.size;
}

/**
 * Free-text search: every meaningful word of the query must appear (as a word
 * prefix, so typing is incremental) somewhere in `haystack`. Accents, punctuation
 * and words like Domaine / Château are ignored, so "domain denis mortet" finds
 * "Denis Mortet — Gevrey-Chambertin 1er Cru Lavaux St. Jacques".
 */
export function matchesQuery(query: string, haystack: string): boolean {
  const q = [...tokens(query)];
  if (q.length === 0) return fold(query) === '' || fold(haystack).includes(fold(query));
  const words = fold(haystack).split(' ');
  return q.every(t => words.some(w => w.startsWith(t)));
}

/**
 * Find the existing producer named inside free text ("domain denis mortet
 * combe du dessus" → Denis Mortet + "combe du dessus"). A producer counts only
 * when ALL words of its name are present; the longest such name wins. Returns
 * null when none, or when two different producers tie (ambiguous — never guess).
 */
export function findProducerInText<P extends { id: string; name: string; aliases?: string[] }>(
  text: string, producers: P[],
): { producer: P; rest: string } | null {
  const qTokens = tokens(text);
  let best: { producer: P; size: number; names: Set<string> } | null = null;
  let tie = false;
  for (const p of producers) {
    for (const n of [p.name, ...(p.aliases ?? [])]) {
      const nt = new Set(producerKey(n).split(' ').filter(Boolean));
      if (nt.size === 0 || ![...nt].every(t => qTokens.has(t))) continue;
      if (!best || nt.size > best.size) { best = { producer: p, size: nt.size, names: nt }; tie = false; }
      else if (nt.size === best.size && best.producer.id !== p.id) tie = true;
    }
  }
  if (!best || tie) return null;
  const drop = best.names;
  const rest = text.trim().split(/\s+/)
    // Remove only the producer's own words and estate prefixes; keep small
    // words that belong to the cuvée ("Combe du Dessus", "Clos de la Roche").
    .filter(w => { const f = fold(w); return f !== '' && !drop.has(f) && !ESTATE_WORDS.has(f) && !PRODUCER_NOISE.has(f); })
    .join(' ');
  return { producer: best.producer, rest };
}
